/**
 * Cmarkdownimage Component
 * Markdown adapter for a parsed image link (`![alt](url)`). Delegates the inline
 * thumbnail + lightbox to the shared Cimagepreview; with no `onClick`, clicking
 * opens Cimagepreview's built-in lightbox.
 */

"use client";

import {
  createContext,
  useContext,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Cimagepreview } from "../Cimagepreview";

type MarkdownImageSourceResolver = (source: string) => string;

const MarkdownImageSourceContext =
  createContext<MarkdownImageSourceResolver | null>(null);

export function CmarkdownImageSourceProvider({
  children,
  resolveSource,
}: {
  readonly children: ReactNode;
  readonly resolveSource: MarkdownImageSourceResolver;
}) {
  return (
    <MarkdownImageSourceContext.Provider value={resolveSource}>
      {children}
    </MarkdownImageSourceContext.Provider>
  );
}

export interface CmarkdownimageProps {
  src: string;
  alt: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
}

export function Cmarkdownimage({
  src,
  alt,
  title,
  className,
  style,
}: CmarkdownimageProps) {
  const resolveSource = useContext(MarkdownImageSourceContext);
  const renderedSource = resolveSource?.(src) ?? src;
  return (
    <span
      data-rendered-source={renderedSource}
      data-source={src}
      style={{ display: "contents" }}
    >
      <Cimagepreview
        src={renderedSource}
        alt={alt}
        title={title}
        className={className}
        style={style}
      />
    </span>
  );
}
